import React, { createContext, useRef, useContext, useCallback, ReactNode } from "react";

interface AnimationControls {
    start: (animationProps?: any) => Promise<any>;
}

interface AnimationContextType {
    registerAnimation: (key: string, controls: AnimationControls) => void;
    startAnimation: (key: string, animationProps?: any) => Promise<any>;
}

// Create a context
const AnimationContext = createContext<AnimationContextType | undefined>(undefined);

interface AnimationProviderProps {
    children: ReactNode;
}

export const AnimationProvider = ({ children }: AnimationProviderProps) => {
    // Store animations in a ref
    const animations = useRef<Record<string, AnimationControls>>({}); // { [key]: { controls, promise, resolve } }

    // Register an animation
    const registerAnimation = useCallback((key: string, controls: AnimationControls) => {
        if (animations.current[key]) return; // Avoid overwriting if already registered

        animations.current[key] = controls;
    }, []);

    // Start an animation and await its completion
    const startAnimation = useCallback(async (key: string, animationProps?: any) => {
        const animation = animations.current[key];
        if (!animation) {
            console.warn(`Animation with key "${key}" not found.`);
            return;
        }

        return animation.start(animationProps);
    }, []);

    return (
        <AnimationContext.Provider
            value={{
                registerAnimation,
                startAnimation,
            }}
        >
            {children}
        </AnimationContext.Provider>
    );
};

// Custom hook to use the AnimationContext
export const useAnimationContext = (): AnimationContextType => {
    const context = useContext(AnimationContext);
    if (context === undefined) {
        throw new Error("useAnimationContext must be used within an AnimationProvider");
    }
    return context;
};
